import React, { useState, useEffect } from 'react';
import {
  Monitor, Plus, Search, Filter, Edit, Trash2, User,
  MapPin, Building, Calendar, DollarSign, AlertCircle,
  CheckCircle, XCircle, Eye, Download, FileText, UserPlus, UserMinus, X, Upload, Settings,
  History, Clock, Users, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Laptop, Camera, Copy
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { showGlobalNotification } from '../hooks/useNotifications';
import { useGlobalSearch } from '../hooks/useGlobalSearch';
import { useLanguage } from '../contexts/LanguageContext';
import Modal from './Modal';

const API_BASE_URL = import.meta.env.VITE_API_URL;

interface MonitorItem {
  id: number;
  inventory_number: string;
  brand_id?: number;
  model_id?: number;
  brand: string;
  model: string;
  serial_number: string;
  screen_size?: string;
  resolution?: string;
  panel_type?: string;
  purchase_date?: string;
  warranty_end_date?: string;
  invoice_url?: string;
  purchase_price?: number;
  currency?: string;
  location_id?: number;
  location_name?: string;
  company_id?: number;
  company_name?: string;
  assigned_user_id?: number;
  assigned_user_name?: string;
  assigned_user_email?: string;
  assigned_computer_id?: number;
  assigned_computer_inventory?: string;
  status: 'active' | 'inactive' | 'under_repair' | 'damaged' | 'retired' | 'scrapped';
  notes?: string;
  brand_name?: string;
  model_name?: string;
  brand_logo?: string;
  model_image?: string;
  model_screen_size?: string;
  model_resolution?: string;
  model_panel_type?: string;
  created_at: string;
  created_by_name?: string;
}

interface Brand {
  id: number;
  name: string;
  logo_url?: string;
  description?: string;
  is_active: boolean;
}

interface Model {
  id: number;
  brand_id: number;
  name: string;
  image_url?: string;
  screen_size?: string;
  resolution?: string;
  panel_type?: string;
  refresh_rate?: string;
  response_time?: string;
  connectivity?: string;
  has_speaker?: boolean;
  has_usb_hub?: boolean;
  is_curved?: boolean;
  description?: string;
  is_active: boolean;
  brand_name?: string;
}

interface Company {
  id: number;
  name: string;
}

interface Location {
  id: number;
  name: string;
}

interface UserItem {
  id: number;
  name: string;
  email: string;
}

interface Computer {
  id: number;
  inventory_number: string;
  brand: string;
  model: string;
}

interface MonitorHistory {
  id: number;
  monitor_id: number;
  action_type: 'create' | 'update' | 'assign' | 'unassign' | 'delete';
  changed_fields: string | null;
  old_values: string | null;
  new_values: string | null;
  performed_by_user_id: number | null;
  performed_by_user_name: string | null;
  assigned_user_id: number | null;
  assigned_user_name: string | null;
  notes: string | null;
  created_at: string;
}

const MonitorsPage: React.FC = () => {
  const { t, language } = useLanguage();
  const [monitors, setMonitors] = useState<MonitorItem[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [models, setModels] = useState<Model[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [users, setUsers] = useState<UserItem[]>([]);
  const [computers, setComputers] = useState<Computer[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showBrandModal, setShowBrandModal] = useState(false);
  const [editingMonitor, setEditingMonitor] = useState<MonitorItem | null>(null);
  const [editingBrand, setEditingBrand] = useState<Brand | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  useGlobalSearch('monitors', setSearchTerm);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterCompany, setFilterCompany] = useState<string>('all');
  const [selectedMonitor, setSelectedMonitor] = useState<MonitorItem | null>(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [selectedComputerId, setSelectedComputerId] = useState<number | null>(null);
  const [filteredModels, setFilteredModels] = useState<Model[]>([]);
  const [userSearchTerm, setUserSearchTerm] = useState('');

  // Atama formu modal states
  const [showAssignmentFormModal, setShowAssignmentFormModal] = useState(false);
  const [assignmentFormNotes, setAssignmentFormNotes] = useState('');
  const [pendingAssignment, setPendingAssignment] = useState<{
    monitor: MonitorItem | null;
    userId: string;
    userName: string;
    computerId: string;
  } | null>(null);

  // Atama kaldırma modal states
  const [showUnassignModal, setShowUnassignModal] = useState(false);
  const [unassignmentFormNotes, setUnassignmentFormNotes] = useState('');
  const [pendingUnassignment, setPendingUnassignment] = useState<MonitorItem | null>(null);
  const [unassignmentPhotos, setUnassignmentPhotos] = useState<string[]>([]);

  // Location filter ve History modal states
  const [locationFilter, setLocationFilter] = useState<string>('all');
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [monitorHistory, setMonitorHistory] = useState<MonitorHistory[]>([]);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Sorting states
  const [sortField, setSortField] = useState<string>('inventory_number');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const [selectedMonitorForHistory, setSelectedMonitorForHistory] = useState<MonitorItem | null>(null);

  const [monitorData, setMonitorData] = useState({
    inventory_number: '',
    brand_id: null as number | null,
    model_id: null as number | null,
    brand: '',
    model: '',
    serial_number: '',
    screen_size: '',
    resolution: '',
    panel_type: '',
    purchase_date: '',
    warranty_end_date: '',
    purchase_price: '',
    currency: 'TRY',
    location_id: null as number | null,
    company_id: null as number | null,
    assigned_user_id: null as number | null,
    assigned_computer_id: null as number | null,
    status: 'active' as const,
    notes: ''
  });

  const [brandData, setBrandData] = useState({
    name: '',
    description: '',
    is_active: true,
    logo: null as File | null
  });

  // Hızlı kopyalama modal state'leri
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [copySource, setCopySource] = useState<MonitorItem | null>(null);
  const [copyFormData, setCopyFormData] = useState({
    inventory_number: '',
    serial_number: '',
    company_id: null as number | null,
    location_id: null as number | null,
    assigned_user_id: null as number | null,
  });
  const [copyUserSearch, setCopyUserSearch] = useState('');
  const [copying, setCopying] = useState(false);

  // Hızlı model ekleme modal state'leri
  const [showQuickModelModal, setShowQuickModelModal] = useState(false);
  const [quickModelData, setQuickModelData] = useState({
    name: '',
    screen_size: '',
    resolution: '',
    panel_type: '',
    refresh_rate: '',
    connectivity: '',
    has_speaker: false,
    has_usb_hub: false,
    is_curved: false
  });
  const [savingQuickModel, setSavingQuickModel] = useState(false);

  const statusOptions = [
    { value: 'active', label: t('monitor.statusActive'), color: 'bg-green-100 text-green-800' },
    { value: 'inactive', label: t('monitor.statusInactive'), color: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200' },
    { value: 'under_repair', label: t('monitor.statusRepair'), color: 'bg-yellow-100 text-yellow-800' },
    { value: 'damaged', label: t('monitor.statusDamaged'), color: 'bg-red-100 text-red-800' },
    { value: 'retired', label: t('monitor.statusRetired'), color: 'bg-purple-100 text-purple-800' },
    { value: 'scrapped', label: t('monitor.statusScrapped'), color: 'bg-orange-100 text-orange-800' }
  ];

  const panelTypes = ['IPS', 'VA', 'TN', 'OLED', 'Mini-LED'];
  const resolutionOptions = ['1920x1080 (FHD)', '2560x1440 (QHD)', '3840x2160 (4K)', '5120x2880 (5K)', '1920x1200 (WUXGA)', '2560x1080 (UW-FHD)', '3440x1440 (UW-QHD)'];
  const screenSizes = ['21.5"', '23.8"', '24"', '27"', '28"', '32"', '34"', '38"', '43"', '49"'];

  // API calls
  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      const headers = { 'Authorization': `Bearer ${token}` };

      const [monitorsRes, brandsRes, modelsRes, companiesRes, locationsRes, usersRes, computersRes] = await Promise.all([
        fetch(`${API_BASE_URL}/monitors`, { headers }),
        fetch(`${API_BASE_URL}/monitors/brands`, { headers }),
        fetch(`${API_BASE_URL}/monitors/models`, { headers }),
        fetch(`${API_BASE_URL}/companies`, { headers }),
        fetch(`${API_BASE_URL}/locations`, { headers }),
        fetch(`${API_BASE_URL}/users`, { headers }),
        fetch(`${API_BASE_URL}/computers`, { headers })
      ]);

      if (monitorsRes.ok) {
        const data = await monitorsRes.json();
        setMonitors(Array.isArray(data) ? data : data.data || []);
      }
      if (brandsRes.ok) {
        const data = await brandsRes.json();
        setBrands(Array.isArray(data) ? data : data.data || []);
      }
      if (modelsRes.ok) {
        const data = await modelsRes.json();
        setModels(Array.isArray(data) ? data : data.data || []);
      }
      if (companiesRes.ok) {
        const data = await companiesRes.json();
        setCompanies(Array.isArray(data) ? data : data.data || data.companies || []);
      }
      if (locationsRes.ok) {
        const data = await locationsRes.json();
        setLocations(Array.isArray(data) ? data : data.data || data.locations || []);
      }
      if (usersRes.ok) {
        const data = await usersRes.json();
        setUsers(Array.isArray(data) ? data : data.users || data.data || []);
      }
      if (computersRes.ok) {
        const data = await computersRes.json();
        setComputers(Array.isArray(data) ? data : data.data || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      showGlobalNotification.error(t('common.error'), t('monitor.errorData'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (monitorData.brand_id) {
      const filtered = models.filter(m => m.brand_id === monitorData.brand_id && m.is_active);
      setFilteredModels(filtered);
    } else {
      setFilteredModels([]);
    }
  }, [monitorData.brand_id, models]);

  const handleBrandChange = (brandId: number | null) => {
    if (brandId) {
      const brand = brands.find(b => b.id === brandId);
      setMonitorData({
        ...monitorData,
        brand_id: brandId,
        brand: brand?.name || '',
        model_id: null,
        model: ''
      });
    } else {
      setMonitorData({
        ...monitorData,
        brand_id: null,
        brand: '',
        model_id: null,
        model: ''
      });
    }
  };

  const handleModelChange = (modelId: number | null) => {
    if (modelId) {
      const model = models.find(m => m.id === modelId);
      setMonitorData({
        ...monitorData,
        model_id: modelId,
        model: model?.name || '',
        screen_size: model?.screen_size || monitorData.screen_size,
        resolution: model?.resolution || monitorData.resolution,
        panel_type: model?.panel_type || monitorData.panel_type
      });
    } else {
      setMonitorData({
        ...monitorData,
        model_id: null,
        model: ''
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('auth_token');
      const formData = new FormData();

      Object.entries(monitorData).forEach(([key, value]) => {
        if (value !== null && value !== '') {
          formData.append(key, String(value));
        }
      });

      const url = editingMonitor
        ? `${API_BASE_URL}/monitors/${editingMonitor.id}`
        : `${API_BASE_URL}/monitors`;

      const response = await fetch(url, {
        method: editingMonitor ? 'PUT' : 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        showGlobalNotification.success(t('common.success'), editingMonitor ? t('monitor.successUpdated') : t('monitor.successCreated'));
        setShowModal(false);
        resetForm();
        fetchData();
      } else {
        const error = await response.json();
        showGlobalNotification.error(t('common.error'), error.error || t('common.operationFailed'));
      }
    } catch (error) {
      console.error('Error saving monitor:', error);
      showGlobalNotification.error(t('common.error'), t('monitor.errorSave'));
    }
  };

  const handleDelete = async (monitor: MonitorItem) => {
    if (!window.confirm(t('monitor.confirmDelete', { invNo: monitor.inventory_number }))) {
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_BASE_URL}/monitors/${monitor.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        showGlobalNotification.success(t('common.success'), t('monitor.successDeleted'));
        fetchData();
      } else {
        const error = await response.json();
        showGlobalNotification.error(t('common.error'), error.error || t('common.operationFailed'));
      }
    } catch (error) {
      console.error('Error deleting monitor:', error);
      showGlobalNotification.error(t('common.error'), t('monitor.errorDelete'));
    }
  };

  const handleCopyOpen = (monitor: MonitorItem) => {
    setCopySource(monitor);
    setCopyFormData({ inventory_number: '', serial_number: '', company_id: monitor.company_id || null, location_id: monitor.location_id || null, assigned_user_id: null });
    setCopyUserSearch('');
    setShowCopyModal(true);
  };

  const handleCopySubmit = async () => {
    if (!copySource) return;
    if (!copyFormData.inventory_number.trim()) {
      showGlobalNotification.error(t('common.error'), t('monitor.errorInventoryRequired'));
      return;
    }
    if (!copyFormData.serial_number.trim()) {
      showGlobalNotification.error(t('common.error'), t('monitor.errorSerialRequired'));
      return;
    }
    try {
      setCopying(true);
      const token = localStorage.getItem('auth_token');
      const body = {
        inventory_number: copyFormData.inventory_number.trim(),
        serial_number: copyFormData.serial_number.trim(),
        brand_id: copySource.brand_id || null,
        model_id: copySource.model_id || null,
        brand: copySource.brand,
        model: copySource.model,
        screen_size: copySource.screen_size || '',
        resolution: copySource.resolution || '',
        panel_type: copySource.panel_type || '',
        purchase_price: copySource.purchase_price || null,
        currency: copySource.currency || 'TRY',
        purchase_date: copySource.purchase_date?.split('T')[0] || null,
        warranty_end_date: copySource.warranty_end_date?.split('T')[0] || null,
        company_id: copyFormData.company_id || null,
        location_id: copyFormData.location_id || null,
        assigned_user_id: copyFormData.assigned_user_id || null,
        status: 'active',
        notes: `${copySource.inventory_number} numaralı monitörden kopyalandı`
      };
      const res = await fetch(`${API_BASE_URL}/monitors`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(body)
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || t('common.operationFailed'));
      }
      showGlobalNotification.success(t('common.success'), t('monitor.successCopied', { invNo: copyFormData.inventory_number }));
      setShowCopyModal(false);
      setCopySource(null);
      fetchData();
    } catch (error: any) {
      showGlobalNotification.error(t('common.error'), error.message);
    } finally {
      setCopying(false);
    }
  };

  const handleEdit = (monitor: MonitorItem) => {
    setEditingMonitor(monitor);
    setMonitorData({
      inventory_number: monitor.inventory_number,
      brand_id: monitor.brand_id || null,
      model_id: monitor.model_id || null,
      brand: monitor.brand,
      model: monitor.model,
      serial_number: monitor.serial_number,
      screen_size: monitor.screen_size || '',
      resolution: monitor.resolution || '',
      panel_type: monitor.panel_type || '',
      purchase_date: monitor.purchase_date?.split('T')[0] || '',
      warranty_end_date: monitor.warranty_end_date?.split('T')[0] || '',
      purchase_price: monitor.purchase_price?.toString() || '',
      currency: monitor.currency || 'TRY',
      location_id: monitor.location_id || null,
      company_id: monitor.company_id || null,
      assigned_user_id: monitor.assigned_user_id || null,
      assigned_computer_id: monitor.assigned_computer_id || null,
      status: monitor.status,
      notes: monitor.notes || ''
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingMonitor(null);
    setMonitorData({
      inventory_number: '',
      brand_id: null,
      model_id: null,
      brand: '',
      model: '',
      serial_number: '',
      screen_size: '',
      resolution: '',
      panel_type: '',
      purchase_date: '',
      warranty_end_date: '',
      purchase_price: '',
      currency: 'TRY',
      location_id: null,
      company_id: null,
      assigned_user_id: null,
      assigned_computer_id: null,
      status: 'active',
      notes: ''
    });
  };

  // Brand functions
  const handleBrandSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('auth_token');
      const formData = new FormData();
      formData.append('name', brandData.name);
      formData.append('description', brandData.description);
      formData.append('is_active', String(brandData.is_active));
      if (brandData.logo) {
        formData.append('logo', brandData.logo);
      }

      const url = editingBrand
        ? `${API_BASE_URL}/monitors/brands/${editingBrand.id}`
        : `${API_BASE_URL}/monitors/brands`;

      const response = await fetch(url, {
        method: editingBrand ? 'PUT' : 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        showGlobalNotification.success(
          t('common.success'),
          editingBrand ? t('monitor.successBrandUpdated') : t('monitor.successBrandCreated')
        );
        setShowBrandModal(false);
        setBrandData({ name: '', description: '', is_active: true, logo: null });
        setEditingBrand(null);
        fetchData();
      } else {
        const error = await response.json();
        showGlobalNotification.error(t('common.error'), error.error || t('common.operationFailed'));
      }
    } catch (error) {
      console.error('Error saving brand:', error);
      showGlobalNotification.error(t('common.error'), t('monitor.errorBrandSave'));
    }
  };

  // Hızlı model ekleme fonksiyonu
  const handleQuickModelSave = async () => {
    if (!monitorData.brand_id || !quickModelData.name.trim()) {
      showGlobalNotification.error(t('common.error'), t('monitor.errorModelRequired'));
      return;
    }

    try {
      setSavingQuickModel(true);
      const token = localStorage.getItem('auth_token');

      const formData = new FormData();
      formData.append('brand_id', String(monitorData.brand_id));
      formData.append('name', quickModelData.name.trim());
      formData.append('screen_size', quickModelData.screen_size);
      formData.append('resolution', quickModelData.resolution);
      formData.append('panel_type', quickModelData.panel_type);
      formData.append('refresh_rate', quickModelData.refresh_rate);
      formData.append('connectivity', quickModelData.connectivity);
      formData.append('has_speaker', String(quickModelData.has_speaker));
      formData.append('has_usb_hub', String(quickModelData.has_usb_hub));
      formData.append('is_curved', String(quickModelData.is_curved));
      formData.append('is_active', 'true');

      const response = await fetch(`${API_BASE_URL}/monitors/models`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        const newModel = await response.json();
        showGlobalNotification.success(t('common.success'), t('monitor.successModelCreated'));

        // Modelleri güncelle
        setModels(prev => [...prev, newModel]);

        // Yeni modeli seç
        setMonitorData(prev => ({
          ...prev,
          model_id: newModel.id,
          model: newModel.name
        }));

        // Modal'ı kapat ve formu sıfırla
        setShowQuickModelModal(false);
        setQuickModelData({
          name: '',
          screen_size: '',
          resolution: '',
          panel_type: '',
          refresh_rate: '',
          connectivity: '',
          has_speaker: false,
          has_usb_hub: false,
          is_curved: false
        });
      } else {
        const error = await response.json();
        showGlobalNotification.error(t('common.error'), error.error || t('monitor.errorModelSave'));
      }
    } catch (error) {
      console.error('Error saving quick model:', error);
      showGlobalNotification.error(t('common.error'), t('monitor.errorModelSave'));
    } finally {
      setSavingQuickModel(false);
    }
  };

  // Assignment functions
  const handleAssign = async () => {
    if (!pendingAssignment?.monitor || !pendingAssignment.userId) return;

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_BASE_URL}/monitors/${pendingAssignment.monitor.id}/assign`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          user_id: pendingAssignment.userId,
          computer_id: pendingAssignment.computerId || null,
          notes: assignmentFormNotes
        })
      });

      if (response.ok) {
        showGlobalNotification.success(t('common.success'), t('monitor.successAssigned'));
        setShowAssignmentFormModal(false);
        setPendingAssignment(null);
        setAssignmentFormNotes('');
        fetchData();
      } else {
        const error = await response.json();
        showGlobalNotification.error(t('common.error'), error.error || t('common.operationFailed'));
      }
    } catch (error) {
      console.error('Error assigning monitor:', error);
      showGlobalNotification.error(t('common.error'), t('monitor.errorAssign'));
    }
  };

  const handleAssignWithForm = async () => {
    if (!pendingAssignment?.monitor || !pendingAssignment.userId) return;
    const token = localStorage.getItem('auth_token');
    const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
    try {
      // 1. Zimmet formu oluştur
      const formRes = await fetch(`${API_BASE_URL}/assignment-forms`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          monitor_id: pendingAssignment.monitor.id,
          assigned_user_id: pendingAssignment.userId,
          assigned_by_user_id: currentUser.id,
          form_type: 'assignment',
          notes: assignmentFormNotes
        })
      });
      if (!formRes.ok) {
        const err = await formRes.json().catch(() => ({}));
        throw new Error(err.error || 'Zimmet formu oluşturulamadı');
      }
      const formData = await formRes.json();

      // 2. Monitörü ata
      const assignRes = await fetch(`${API_BASE_URL}/monitors/${pendingAssignment.monitor.id}/assign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          user_id: pendingAssignment.userId,
          computer_id: pendingAssignment.computerId || null,
          notes: assignmentFormNotes
        })
      });
      if (!assignRes.ok) {
        const err = await assignRes.json().catch(() => ({}));
        throw new Error(err.error || t('common.operationFailed'));
      }

      showGlobalNotification.success(t('common.success'), t('monitor.successFormCreated', { formNo: formData.form_number }));

      setShowAssignmentFormModal(false);
      setPendingAssignment(null);
      setAssignmentFormNotes('');
      // Formu yeni sekmede aç, sayfada kalarak listeyi yenile
      window.open(`/assignment-forms/${formData.id}`, '_blank');
      fetchData();
    } catch (error: any) {
      showGlobalNotification.error(t('common.error'), error.message);
    }
  };

  const uploadReturnPhotos = async (formId: number, photos: string[]) => {
    if (photos.length === 0) return;
    const token = localStorage.getItem('auth_token');
    const fd = new FormData();
    photos.forEach((dataUrl, i) => {
      const arr = dataUrl.split(',');
      const mime = arr[0].match(/:(.*?);/)![1];
      const bstr = atob(arr[1]);
      const u8arr = new Uint8Array(bstr.length);
      for (let n = 0; n < bstr.length; n++) u8arr[n] = bstr.charCodeAt(n);
      fd.append('photos', new Blob([u8arr], { type: mime }), `photo-${i + 1}.jpg`);
    });
    await fetch(`${API_BASE_URL}/assignment-forms/${formId}/return-photos`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: fd
    });
  };

  const handleUnassignWithForm = async () => {
    if (!pendingUnassignment) return;
    try {
      const token = localStorage.getItem('auth_token');
      // Geri zimmet formu oluştur
      const formResponse = await fetch(`${API_BASE_URL}/assignment-forms`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          monitor_id: pendingUnassignment.id,
          assigned_user_id: pendingUnassignment.assigned_user_id,
          assigned_by_user_id: JSON.parse(localStorage.getItem('user') || '{}').id,
          form_type: 'return',
          notes: unassignmentFormNotes
        })
      });
      if (!formResponse.ok) {
        const errData = await formResponse.json().catch(() => ({}));
        throw new Error(errData.error || t('common.operationFailed'));
      }
      const formData = await formResponse.json();
      // Fotoğrafları yükle
      if (unassignmentPhotos.length > 0) {
        await uploadReturnPhotos(formData.id, unassignmentPhotos);
      }
      // Atamayı kaldır
      await fetch(`${API_BASE_URL}/monitors/${pendingUnassignment.id}/unassign`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes: unassignmentFormNotes })
      });
      showGlobalNotification.success(t('common.success'), t('monitor.successReturnForm', { formNo: formData.form_number }));
      setShowUnassignModal(false);
      setPendingUnassignment(null);
      setUnassignmentFormNotes('');
      setUnassignmentPhotos([]);
      window.location.href = '/assignment-forms';
    } catch (error: any) {
      showGlobalNotification.error(t('common.error'), error.message);
    }
  };

  const handleUnassign = async () => {
    if (!pendingUnassignment) return;

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_BASE_URL}/monitors/${pendingUnassignment.id}/unassign`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ notes: unassignmentFormNotes })
      });

      if (response.ok) {
        showGlobalNotification.success(t('common.success'), t('monitor.successUnassigned'));
        setShowUnassignModal(false);
        setPendingUnassignment(null);
        setUnassignmentFormNotes('');
        setUnassignmentPhotos([]);
        fetchData();
      } else {
        const error = await response.json();
        showGlobalNotification.error(t('common.error'), error.error || t('common.operationFailed'));
      }
    } catch (error) {
      console.error('Error unassigning monitor:', error);
      showGlobalNotification.error(t('common.error'), t('monitor.errorUnassign'));
    }
  };

  const addUnassignmentPhoto = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    const remaining = 4 - unassignmentPhotos.length;
    const toProcess = Math.min(files.length, remaining);
    for (let i = 0; i < toProcess; i++) {
      const file = files[i];
      const reader = new FileReader();
      reader.onload = (ev) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const maxW = 1200, maxH = 1600;
          let { width, height } = img;
          if (width > maxW || height > maxH) {
            const ratio = Math.min(maxW / width, maxH / height);
            width = Math.round(width * ratio);
            height = Math.round(height * ratio);
          }
          canvas.width = width;
          canvas.height = height;
          canvas.getContext('2d')!.drawImage(img, 0, 0, width, height);
          const dataUrl = canvas.toDataURL('image/jpeg', 0.75);
          setUnassignmentPhotos(prev => prev.length < 4 ? [...prev, dataUrl] : prev);
        };
        img.src = ev.target?.result as string;
      };
      reader.readAsDataURL(file);
    }
    e.target.value = '';
  };

  // History functions
  const fetchHistory = async (monitor: MonitorItem) => {
    setSelectedMonitorForHistory(monitor);
    setHistoryLoading(true);
    setShowHistoryModal(true);

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_BASE_URL}/monitors/${monitor.id}/history`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setMonitorHistory(await response.json());
      }
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
      setHistoryLoading(false);
    }
  };

  // Export to Excel
  const exportToExcel = () => {
    const exportData = filteredMonitors.map(m => ({
      [t('monitor.excelInventory')]: m.inventory_number,
      [t('monitor.excelBrand')]: m.brand,
      [t('monitor.excelModel')]: m.model,
      [t('monitor.excelSerial')]: m.serial_number,
      [t('monitor.excelScreenSize')]: m.screen_size || m.model_screen_size || '-',
      [t('monitor.excelResolution')]: m.resolution || m.model_resolution || '-',
      [t('monitor.excelPanelType')]: m.panel_type || m.model_panel_type || '-',
      [t('monitor.excelLocation')]: m.location_name || '-',
      [t('monitor.excelCompany')]: m.company_name || '-',
      [t('monitor.excelAssignedUser')]: m.assigned_user_name || '-',
      [t('monitor.excelStatus')]: statusOptions.find(s => s.value === m.status)?.label || m.status,
      [t('monitor.excelPurchaseDate')]: m.purchase_date?.split('T')[0] || '-',
      [t('monitor.excelWarranty')]: m.warranty_end_date?.split('T')[0] || '-'
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, t('monitor.title'));
    XLSX.writeFile(wb, `monitorler_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // Filtering and sorting
  const filteredMonitors = monitors
    .filter(m => {
      const matchesSearch = searchTerm === '' ||
        m.inventory_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        m.brand.toLowerCase().includes(searchTerm.toLowerCase()) ||
        m.model.toLowerCase().includes(searchTerm.toLowerCase()) ||
        m.serial_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        m.assigned_user_name?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus = filterStatus === 'all' || m.status === filterStatus;
      const matchesCompany = filterCompany === 'all' || m.company_id?.toString() === filterCompany;
      const matchesLocation = locationFilter === 'all' || m.location_id?.toString() === locationFilter;

      return matchesSearch && matchesStatus && matchesCompany && matchesLocation;
    })
    .sort((a, b) => {
      let aVal = a[sortField as keyof MonitorItem];
      let bVal = b[sortField as keyof MonitorItem];

      if (aVal === null || aVal === undefined) aVal = '';
      if (bVal === null || bVal === undefined) bVal = '';

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortDirection === 'asc'
          ? aVal.localeCompare(bVal, 'tr')
          : bVal.localeCompare(aVal, 'tr');
      }

      return sortDirection === 'asc'
        ? (aVal as number) - (bVal as number)
        : (bVal as number) - (aVal as number);
    });

  // Pagination
  const totalPages = Math.ceil(filteredMonitors.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedMonitors = filteredMonitors.slice(startIndex, startIndex + itemsPerPage);

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortIcon = ({ field }: { field: string }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />;
  };

  const getStatusBadge = (status: string) => {
    const option = statusOptions.find(s => s.value === status);
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${option?.color || 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'}`}>
        {option?.label || status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div className="flex items-center space-x-3">
          <Monitor className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 dark:text-white">{t('monitor.title')}</h1>
            <p className="text-gray-500 dark:text-gray-400 text-sm">
              {t('monitor.subtitle', { count: filteredMonitors.length })}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={exportToExcel}
            className="flex items-center px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
          >
            <Download className="h-4 w-4 mr-2" />
            Excel
          </button>
          <button
            onClick={() => setShowBrandModal(true)}
            className="flex items-center px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm"
          >
            <Settings className="h-4 w-4 mr-2" />
            {t('monitor.brandsBtn')}
          </button>
          <button
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
          >
            <Plus className="h-5 w-5 mr-2" />
            {t('monitor.newMonitor')}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder={t('monitor.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
          >
            <option value="all">{t('monitor.filterAllStatuses')}</option>
            {statusOptions.map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          <select
            value={filterCompany}
            onChange={(e) => setFilterCompany(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
          >
            <option value="all">{t('monitor.filterAllCompanies')}</option>
            {companies.map(company => (
              <option key={company.id} value={company.id}>{company.name}</option>
            ))}
          </select>
          <select
            value={locationFilter}
            onChange={(e) => setLocationFilter(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
          >
            <option value="all">{t('monitor.filterAllLocations')}</option>
            {locations.map(location => (
              <option key={location.id} value={location.id}>{location.name}</option>
            ))}
          </select>
          <div className="flex items-center justify-end text-sm text-gray-500 dark:text-gray-400">
            <Filter className="h-4 w-4 mr-2" />
            {t('monitor.resultsCount', { count: filteredMonitors.length })}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700 dark:bg-gray-900">
              <tr>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 dark:bg-gray-700 dark:hover:bg-gray-800"
                  onClick={() => handleSort('inventory_number')}
                >
                  <div className="flex items-center">
                    {t('monitor.colInventory')}
                    <SortIcon field="inventory_number" />
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('monitor.colBrandModel')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('monitor.colSpecs')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('monitor.colLocation')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('monitor.colUser')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('monitor.colStatus')}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('monitor.colActions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {paginatedMonitors.map((monitor) => (
                <tr key={monitor.id} className="hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100 dark:text-white">
                      {monitor.inventory_number}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      SN: {monitor.serial_number}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {monitor.brand_logo ? (
                        <img
                          src={monitor.brand_logo}
                          alt={monitor.brand}
                          className="h-8 w-8 rounded object-contain mr-3"
                        />
                      ) : (
                        <div className="h-8 w-8 bg-gray-200 dark:bg-gray-600 rounded flex items-center justify-center mr-3">
                          <Monitor className="h-4 w-4 text-gray-400" />
                        </div>
                      )}
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100 dark:text-white">{monitor.brand}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{monitor.model}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-gray-100 dark:text-white">
                      {monitor.screen_size || monitor.model_screen_size || '-'}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {monitor.resolution || monitor.model_resolution || '-'} • {monitor.panel_type || monitor.model_panel_type || '-'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center text-sm text-gray-900 dark:text-gray-100 dark:text-white">
                      <MapPin className="h-4 w-4 mr-1 text-gray-400" />
                      {monitor.location_name || '-'}
                    </div>
                    <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                      <Building className="h-3 w-3 mr-1" />
                      {monitor.company_name || '-'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {monitor.assigned_user_name ? (
                      <div>
                        <div className="flex items-center text-sm text-gray-900 dark:text-gray-100 dark:text-white">
                          <User className="h-4 w-4 mr-1 text-gray-400" />
                          {monitor.assigned_user_name}
                        </div>
                        {monitor.assigned_computer_inventory && (
                          <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                            <Laptop className="h-3 w-3 mr-1" />
                            {monitor.assigned_computer_inventory}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(monitor.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      {monitor.assigned_user_id ? (
                        <button
                          onClick={() => {
                            setPendingUnassignment(monitor);
                            setShowUnassignModal(true);
                          }}
                          className="p-1 text-orange-600 hover:bg-orange-100 rounded"
                          title={t('monitor.titleUnassign')}
                        >
                          <UserMinus className="h-4 w-4" />
                        </button>
                      ) : (
                        <button
                          onClick={() => {
                            setSelectedMonitor(monitor);
                            setShowAssignModal(true);
                          }}
                          className="p-1 text-green-600 hover:bg-green-100 rounded"
                          title={t('monitor.titleAssign')}
                        >
                          <UserPlus className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        onClick={() => fetchHistory(monitor)}
                        className="p-1 text-purple-600 hover:bg-purple-100 rounded"
                        title={t('monitor.titleHistory')}
                      >
                        <History className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleCopyOpen(monitor)}
                        className="p-1 text-orange-500 hover:bg-orange-100 rounded"
                        title={t('monitor.titleCopy')}
                      >
                        <Copy className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleEdit(monitor)}
                        className="p-1 text-primary hover:bg-primary/20 rounded"
                        title={t('common.edit')}
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(monitor)}
                        className="p-1 text-red-600 hover:bg-red-100 rounded"
                        title={t('common.delete')}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white dark:bg-gray-800 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 disabled:opacity-50"
              >
                {t('monitor.pagePrev')}
              </button>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 disabled:opacity-50"
              >
                {t('monitor.pageNext')}
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  {t('monitor.pageInfo', { start: startIndex + 1, end: Math.min(startIndex + itemsPerPage, filteredMonitors.length), total: filteredMonitors.length })}
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 dark:hover:bg-gray-600 disabled:opacity-50"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(page => page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1)
                    .map((page, index, array) => (
                      <React.Fragment key={page}>
                        {index > 0 && array[index - 1] !== page - 1 && (
                          <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 dark:bg-gray-700 text-sm font-medium text-gray-700 dark:text-gray-300">
                            ...
                          </span>
                        )}
                        <button
                          onClick={() => setCurrentPage(page)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            currentPage === page
                              ? 'z-10 bg-primary/10 dark:bg-primary/90 border-primary text-primary dark:text-accent'
                              : 'bg-white dark:bg-gray-800 dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 dark:hover:bg-gray-600'
                          }`}
                        >
                          {page}
                        </button>
                      </React.Fragment>
                    ))}
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 dark:hover:bg-gray-600 disabled:opacity-50"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit Monitor Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 dark:text-white">
                {editingMonitor ? t('monitor.modalTitleEdit') : t('monitor.modalTitleNew')}
              </h2>
              <button
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-500 dark:text-gray-400"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('monitor.labelInventory')}
                  </label>
                  <input
                    type="text"
                    required
                    value={monitorData.inventory_number}
                    onChange={(e) => setMonitorData({ ...monitorData, inventory_number: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                    placeholder="MON-001"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('monitor.labelBrand')}
                  </label>
                  <select
                    value={monitorData.brand_id || ''}
                    onChange={(e) => handleBrandChange(e.target.value ? Number(e.target.value) : null)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">{t('monitor.selectBrand')}</option>
                    {brands.filter(b => b.is_active).map(brand => (
                      <option key={brand.id} value={brand.id}>{brand.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('monitor.labelModel')}
                  </label>
                  {monitorData.brand_id ? (
                    <div className="flex gap-2">
                      <select
                        value={monitorData.model_id || ''}
                        onChange={(e) => handleModelChange(e.target.value ? Number(e.target.value) : null)}
                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                      >
                        <option value="">{t('monitor.selectModel')}</option>
                        {filteredModels.map(model => (
                          <option key={model.id} value={model.id}>{model.name}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => setShowQuickModelModal(true)}
                        className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-1"
                        title={t('monitor.addModelBtn')}
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <input
                      type="text"
                      required
                      value={monitorData.model}
                      onChange={(e) => setMonitorData({ ...monitorData, model: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                      placeholder={t('monitor.modelPlaceholder')}
                    />
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('monitor.labelSerial')}
                  </label>
                  <input
                    type="text"
                    required
                    value={monitorData.serial_number}
                    onChange={(e) => setMonitorData({ ...monitorData, serial_number: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('monitor.labelStatus')}
                  </label>
                  <select
                    value={monitorData.status}
                    onChange={(e) => setMonitorData({ ...monitorData, status: e.target.value as any })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                  >
                    {statusOptions.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Specs */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('monitor.labelScreenSize')}
                  </label>
                  <select
                    value={monitorData.screen_size}
                    onChange={(e) => setMonitorData({ ...monitorData, screen_size: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">{t('monitor.selectDefault')}</option>
                    {screenSizes.map(size => (
                      <option key={size} value={size}>{size}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('monitor.labelResolution')}
                  </label>
                  <select
                    value={monitorData.resolution}
                    onChange={(e) => setMonitorData({ ...monitorData, resolution: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">{t('monitor.selectDefault')}</option>
                    {resolutionOptions.map(res => (
                      <option key={res} value={res}>{res}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('monitor.labelPanelType')}
                  </label>
                  <select
                    value={monitorData.panel_type}
                    onChange={(e) => setMonitorData({ ...monitorData, panel_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">{t('monitor.selectDefault')}</option>
                    {panelTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Location & Company */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('monitor.labelLocation')}
                  </label>
                  <select
                    value={monitorData.location_id || ''}
                    onChange={(e) => setMonitorData({ ...monitorData, location_id: e.target.value ? Number(e.target.value) : null })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">{t('monitor.selectDefault')}</option>
                    {locations.map(loc => (
                      <option key={loc.id} value={loc.id}>{loc.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('monitor.labelCompany')}
                  </label>
                  <select
                    value={monitorData.company_id || ''}
                    onChange={(e) => setMonitorData({ ...monitorData, company_id: e.target.value ? Number(e.target.value) : null })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">{t('monitor.selectDefault')}</option>
                    {companies.map(comp => (
                      <option key={comp.id} value={comp.id}>{comp.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Purchase Info */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('monitor.labelPurchaseDate')}
                  </label>
                  <input
                    type="date"
                    value={monitorData.purchase_date}
                    onChange={(e) => setMonitorData({ ...monitorData, purchase_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('monitor.labelWarranty')}
                  </label>
                  <input
                    type="date"
                    value={monitorData.warranty_end_date}
                    onChange={(e) => setMonitorData({ ...monitorData, warranty_end_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('monitor.labelPurchasePrice')}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={monitorData.purchase_price}
                    onChange={(e) => setMonitorData({ ...monitorData, purchase_price: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('monitor.labelCurrency')}
                  </label>
                  <select
                    value={monitorData.currency}
                    onChange={(e) => setMonitorData({ ...monitorData, currency: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                  >
                    <option value="TRY">TRY</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                  </select>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('monitor.labelNotes')}
                </label>
                <textarea
                  rows={3}
                  value={monitorData.notes}
                  onChange={(e) => setMonitorData({ ...monitorData, notes: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                />
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark"
                >
                  {editingMonitor ? t('monitor.update') : t('common.save')}
                </button>
              </div>
            </form>
          </div>
        </Modal>

      {/* Assign Modal */}
      <Modal isOpen={showAssignModal} onClose={() => setShowAssignModal(false)}>
          {selectedMonitor && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 dark:text-white">
                {t('monitor.assignTitle')}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {selectedMonitor.inventory_number} - {selectedMonitor.brand} {selectedMonitor.model}
              </p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('monitor.userSearchLabel')}
                </label>
                <input
                  type="text"
                  value={userSearchTerm}
                  onChange={(e) => setUserSearchTerm(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                  placeholder={t('monitor.userSearchPlaceholder')}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('monitor.selectUserLabel')}
                </label>
                <select
                  value={selectedUserId || ''}
                  onChange={(e) => setSelectedUserId(e.target.value ? Number(e.target.value) : null)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                >
                  <option value="">{t('monitor.selectDefault')}</option>
                  {users
                    .filter(u =>
                      userSearchTerm === '' ||
                      u.name.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
                      u.email.toLowerCase().includes(userSearchTerm.toLowerCase())
                    )
                    .map(user => (
                      <option key={user.id} value={user.id}>{user.name} ({user.email})</option>
                    ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('monitor.labelComputer')}
                </label>
                <select
                  value={selectedComputerId || ''}
                  onChange={(e) => setSelectedComputerId(e.target.value ? Number(e.target.value) : null)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                >
                  <option value="">{t('monitor.selectDefault')}</option>
                  {computers
                    .filter(c => c.inventory_number)
                    .map(comp => (
                      <option key={comp.id} value={comp.id}>{comp.inventory_number} - {comp.brand} {comp.model}</option>
                    ))}
                </select>
              </div>
            </div>
            <div className="p-6 bg-gray-50 dark:bg-gray-700 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowAssignModal(false);
                  setSelectedMonitor(null);
                  setSelectedUserId(null);
                  setSelectedComputerId(null);
                  setUserSearchTerm('');
                }}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 rounded-lg hover:bg-gray-300"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={() => {
                  if (selectedUserId) {
                    const user = users.find(u => u.id === selectedUserId);
                    setPendingAssignment({
                      monitor: selectedMonitor,
                      userId: String(selectedUserId),
                      userName: user?.name || '',
                      computerId: selectedComputerId ? String(selectedComputerId) : ''
                    });
                    setShowAssignModal(false);
                    setShowAssignmentFormModal(true);
                  }
                }}
                disabled={!selectedUserId}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50"
              >
                {t('monitor.continueBtn')}
              </button>
            </div>
          </div>
          )}
        </Modal>

      {/* Hızlı Kopyalama Modal */}
      <Modal isOpen={showCopyModal} onClose={() => setShowCopyModal(false)}>
        {copySource && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center gap-3">
              <Copy className="w-5 h-5 text-orange-500" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('monitor.copyTitle')}</h2>
            </div>

            <div className="p-6 space-y-4">
              {/* Kaynak bilgisi */}
              <div className="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg text-sm">
                <p className="font-medium text-gray-700 dark:text-gray-300 mb-1">{t('monitor.copySourceLabel')}</p>
                <p className="text-gray-600 dark:text-gray-400">{copySource.brand} {copySource.model}</p>
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {copySource.screen_size && <span>{t('monitor.copyScreenLabel')} {copySource.screen_size}</span>}
                  {copySource.panel_type && <span>{t('monitor.copyPanelLabel')} {copySource.panel_type}</span>}
                  {copySource.resolution && <span>{copySource.resolution}</span>}
                </div>
              </div>

              {/* Envanter No */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('monitor.labelInventory')}
                </label>
                <input
                  type="text"
                  value={copyFormData.inventory_number}
                  onChange={e => setCopyFormData(p => ({ ...p, inventory_number: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white text-sm"
                  placeholder="örn: MON-2024-002"
                  autoFocus
                />
              </div>

              {/* Seri No */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('monitor.labelSerialNo')}
                </label>
                <input
                  type="text"
                  value={copyFormData.serial_number}
                  onChange={e => setCopyFormData(p => ({ ...p, serial_number: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white text-sm"
                  placeholder={t('monitor.serialPlaceholder')}
                />
              </div>

              {/* Şirket */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('monitor.labelCompany')}</label>
                <select
                  value={copyFormData.company_id || ''}
                  onChange={e => setCopyFormData(p => ({ ...p, company_id: e.target.value ? Number(e.target.value) : null, location_id: null }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white text-sm"
                >
                  <option value="">{t('monitor.selectCompany')}</option>
                  {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>

              {/* Lokasyon */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('monitor.labelLocation')}</label>
                <select
                  value={copyFormData.location_id || ''}
                  onChange={e => setCopyFormData(p => ({ ...p, location_id: e.target.value ? Number(e.target.value) : null }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white text-sm"
                >
                  <option value="">{t('monitor.selectLocation')}</option>
                  {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              </div>

              {/* Kullanıcı (opsiyonel) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('monitor.labelAssignUser')} <span className="text-xs text-gray-400">{t('monitor.optional')}</span>
                </label>
                <input
                  type="text"
                  value={copyUserSearch}
                  onChange={e => setCopyUserSearch(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white text-sm"
                  placeholder={t('monitor.userSearchPlaceholder2')}
                />
                {copyUserSearch && (
                  <div className="mt-1 border border-gray-200 dark:border-gray-600 rounded-lg max-h-36 overflow-y-auto bg-white dark:bg-gray-700 shadow-sm">
                    {users
                      .filter(u => u.name.toLowerCase().includes(copyUserSearch.toLowerCase()) || u.email.toLowerCase().includes(copyUserSearch.toLowerCase()))
                      .slice(0, 6)
                      .map(u => (
                        <button
                          key={u.id}
                          type="button"
                          onClick={() => { setCopyFormData(p => ({ ...p, assigned_user_id: u.id })); setCopyUserSearch(u.name); }}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-600 border-b border-gray-100 dark:border-gray-600 last:border-0"
                        >
                          <span className="font-medium text-gray-800 dark:text-gray-200">{u.name}</span>
                          <span className="text-gray-400 text-xs ml-2">{u.email}</span>
                        </button>
                      ))}
                    {users.filter(u => u.name.toLowerCase().includes(copyUserSearch.toLowerCase()) || u.email.toLowerCase().includes(copyUserSearch.toLowerCase())).length === 0 && (
                      <p className="px-3 py-2 text-sm text-gray-400">{t('monitor.userNotFound')}</p>
                    )}
                  </div>
                )}
                {copyFormData.assigned_user_id && (
                  <div className="mt-1 flex items-center gap-2 text-xs text-green-600">
                    <CheckCircle className="w-3 h-3" />
                    {t('monitor.userSelected', { name: users.find(u => u.id === copyFormData.assigned_user_id)?.name || '' })}
                    <button type="button" onClick={() => { setCopyFormData(p => ({ ...p, assigned_user_id: null })); setCopyUserSearch(''); }} className="text-gray-400 hover:text-red-500">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="p-6 bg-gray-50 dark:bg-gray-700 flex justify-end gap-3 rounded-b-lg">
              <button
                onClick={() => setShowCopyModal(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 rounded-lg hover:bg-gray-300 text-sm"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleCopySubmit}
                disabled={copying || !copyFormData.inventory_number.trim() || !copyFormData.serial_number.trim()}
                className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm flex items-center gap-2"
              >
                <Copy className="w-4 h-4" />
                {copying ? t('monitor.copying') : t('monitor.copyBtn')}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Assignment Form Modal */}
      <Modal isOpen={showAssignmentFormModal} onClose={() => setShowAssignmentFormModal(false)}>
          {pendingAssignment && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                {t('monitor.assignFormTitle')}
              </h2>
            </div>
            <div className="p-6 space-y-4">
              <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-sm text-gray-700 dark:text-gray-300">
                <strong>{pendingAssignment.monitor?.inventory_number}</strong> — {pendingAssignment.monitor?.brand} {pendingAssignment.monitor?.model}<br />
                <span className="text-gray-500 dark:text-gray-400">{t('monitor.assignUserLabel')} <strong>{pendingAssignment.userName}</strong></span>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('monitor.noteOptional')}
                </label>
                <textarea
                  rows={3}
                  value={assignmentFormNotes}
                  onChange={(e) => setAssignmentFormNotes(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                  placeholder={t('monitor.notePlaceholder')}
                />
              </div>
            </div>
            <div className="p-6 bg-gray-50 dark:bg-gray-700 flex flex-col sm:flex-row justify-end gap-2">
              <button
                onClick={() => {
                  setShowAssignmentFormModal(false);
                  setPendingAssignment(null);
                  setAssignmentFormNotes('');
                }}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 rounded-lg hover:bg-gray-300 order-last sm:order-first"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleAssign}
                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
              >
                {t('monitor.assignWithoutForm')}
              </button>
              <button
                onClick={handleAssignWithForm}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center gap-2"
              >
                <FileText className="w-4 h-4" />
                {t('monitor.assignWithForm')}
              </button>
            </div>
          </div>
          )}
        </Modal>

      {/* Unassign Modal */}
      <Modal isOpen={showUnassignModal} onClose={() => setShowUnassignModal(false)}>
          {pendingUnassignment && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full max-h-[85vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 dark:text-white">
                {t('monitor.unassignTitle')}
              </h2>
            </div>
            <div className="p-6 space-y-4">
              <div className="p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  <strong>{t('monitor.unassignMonitorLabel')}</strong> {pendingUnassignment.inventory_number} - {pendingUnassignment.brand} {pendingUnassignment.model}
                </p>
                <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">
                  <strong>{t('monitor.assignUserLabel')}</strong> {pendingUnassignment.assigned_user_name}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('monitor.noteOptional2')}
                </label>
                <textarea
                  rows={3}
                  value={unassignmentFormNotes}
                  onChange={(e) => setUnassignmentFormNotes(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                  placeholder={t('monitor.removeReasonPlaceholder')}
                />
              </div>
              {/* Cihaz Fotoğrafları */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('monitor.photosLabel')}
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {unassignmentPhotos.map((photo, idx) => (
                    <div key={idx} className="relative aspect-square">
                      <img src={photo} alt={`Fotoğraf ${idx + 1}`} className="w-full h-full object-cover rounded-md border border-gray-300 dark:border-gray-600" />
                      <button
                        onClick={() => setUnassignmentPhotos(prev => prev.filter((_, i) => i !== idx))}
                        className="absolute top-0.5 right-0.5 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center hover:bg-red-600"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  {unassignmentPhotos.length < 4 && (
                    <label className="aspect-square border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-md flex flex-col items-center justify-center cursor-pointer hover:border-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/10 transition-colors">
                      <Camera className="w-5 h-5 text-gray-400 mb-1" />
                      <span className="text-xs text-gray-400">{t('monitor.addPhoto')}</span>
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        multiple
                        className="hidden"
                        onChange={addUnassignmentPhoto}
                      />
                    </label>
                  )}
                </div>
              </div>
            </div>
            <div className="p-6 bg-gray-50 dark:bg-gray-700 flex flex-col gap-2">
              <div className="flex gap-2">
                <button
                  onClick={handleUnassignWithForm}
                  className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center justify-center gap-2"
                >
                  <FileText className="w-4 h-4" />
                  {t('monitor.withReturnForm')}
                </button>
                <button
                  onClick={handleUnassign}
                  className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark flex items-center justify-center gap-2"
                >
                  <UserMinus className="w-4 h-4" />
                  {t('monitor.withoutForm')}
                </button>
              </div>
              <button
                onClick={() => {
                  setShowUnassignModal(false);
                  setPendingUnassignment(null);
                  setUnassignmentFormNotes('');
                  setUnassignmentPhotos([]);
                }}
                className="w-full px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 rounded-lg hover:bg-gray-300"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
          )}
        </Modal>

      {/* History Modal */}
      <Modal isOpen={showHistoryModal} onClose={() => setShowHistoryModal(false)}>
          {selectedMonitorForHistory && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 dark:text-white">
                  {t('monitor.historyTitle')}
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {selectedMonitorForHistory.inventory_number}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowHistoryModal(false);
                  setSelectedMonitorForHistory(null);
                }}
                className="text-gray-400 hover:text-gray-500 dark:text-gray-400"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {historyLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : monitorHistory.length > 0 ? (
                <div className="space-y-4">
                  {monitorHistory.map((item) => (
                    <div
                      key={item.id}
                      className="border border-gray-200 dark:border-gray-700 rounded-lg p-4"
                    >
                      <div className="flex justify-between items-start mb-2">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          item.action_type === 'create' ? 'bg-green-100 text-green-800' :
                          item.action_type === 'update' ? 'bg-primary/20 text-primary-dark' :
                          item.action_type === 'assign' ? 'bg-purple-100 text-purple-800' :
                          item.action_type === 'unassign' ? 'bg-orange-100 text-orange-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {item.action_type === 'create' ? t('monitor.historyCreated') :
                           item.action_type === 'update' ? t('monitor.historyUpdated') :
                           item.action_type === 'assign' ? t('monitor.historyAssigned') :
                           item.action_type === 'unassign' ? t('monitor.historyUnassigned') :
                           t('monitor.historyDeleted')}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {new Date(item.created_at).toLocaleString(language === 'en' ? 'en-US' : 'tr-TR')}
                        </span>
                      </div>
                      <div className="text-sm text-gray-700 dark:text-gray-300">
                        <p>{t('monitor.historyPerformedBy')} {item.performed_by_user_name || '-'}</p>
                        {item.assigned_user_name && (
                          <p>{t('monitor.historyUser')} {item.assigned_user_name}</p>
                        )}
                        {item.notes && (
                          <p className="mt-1 text-gray-500 dark:text-gray-400">{t('monitor.historyNote')} {item.notes}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-gray-500 dark:text-gray-400 py-8">
                  {t('monitor.historyEmpty')}
                </p>
              )}
            </div>
          </div>
          )}
        </Modal>

      {/* Brand Modal */}
      <Modal isOpen={showBrandModal} onClose={() => setShowBrandModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 dark:text-white">
                {t('monitor.brandsTitle')}
              </h2>
              <button
                onClick={() => {
                  setShowBrandModal(false);
                  setBrandData({ name: '', description: '', is_active: true, logo: null });
                  setEditingBrand(null);
                }}
                className="text-gray-400 hover:text-gray-500 dark:text-gray-400"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6">
              <form onSubmit={handleBrandSubmit} className="space-y-4 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('monitor.brandName')}
                    </label>
                    <input
                      type="text"
                      required
                      value={brandData.name}
                      onChange={(e) => setBrandData({ ...brandData, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('monitor.brandLogo')}
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => setBrandData({ ...brandData, logo: e.target.files?.[0] || null })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={brandData.is_active}
                      onChange={(e) => setBrandData({ ...brandData, is_active: e.target.checked })}
                      className="rounded border-gray-300 dark:border-gray-600 text-primary focus:ring-primary"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{t('monitor.brandActive')}</span>
                  </label>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark"
                  >
                    {editingBrand ? t('monitor.update') : t('monitor.addBtn')}
                  </button>
                </div>
              </form>

              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">{t('monitor.existingBrands')}</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-48 overflow-y-auto">
                  {brands.map(brand => (
                    <div
                      key={brand.id}
                      className={`flex items-center justify-between p-2 rounded border ${brand.is_active ? 'border-gray-200 dark:border-gray-700' : 'border-red-200 dark:border-red-700 opacity-50'}`}
                    >
                      <div className="flex items-center space-x-2">
                        {brand.logo_url ? (
                          <img src={brand.logo_url} alt={brand.name} className="h-6 w-6 object-contain" />
                        ) : (
                          <Monitor className="h-6 w-6 text-gray-400" />
                        )}
                        <span className="text-sm text-gray-900 dark:text-gray-100 dark:text-white truncate">{brand.name}</span>
                      </div>
                      <button
                        onClick={() => {
                          setEditingBrand(brand);
                          setBrandData({
                            name: brand.name,
                            description: brand.description || '',
                            is_active: brand.is_active,
                            logo: null
                          });
                        }}
                        className="p-1 text-primary hover:bg-primary/20 rounded"
                      >
                        <Edit className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </Modal>

      {/* Hızlı Model Ekleme Modal */}
      <Modal isOpen={showQuickModelModal} onClose={() => setShowQuickModelModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full max-h-[85vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 dark:text-white">
                {t('monitor.quickModelTitle')} - {brands.find(b => b.id === monitorData.brand_id)?.name}
              </h2>
              <button
                onClick={() => {
                  setShowQuickModelModal(false);
                  setQuickModelData({
                    name: '',
                    screen_size: '',
                    resolution: '',
                    panel_type: '',
                    refresh_rate: '',
                    connectivity: '',
                    has_speaker: false,
                    has_usb_hub: false,
                    is_curved: false
                  });
                }}
                className="text-gray-400 hover:text-gray-500 dark:text-gray-400"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('monitor.modelName')}
                </label>
                <input
                  type="text"
                  required
                  value={quickModelData.name}
                  onChange={(e) => setQuickModelData({ ...quickModelData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                  placeholder="Örn: U2723QE"
                  autoFocus
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('monitor.labelScreenSize')}
                  </label>
                  <select
                    value={quickModelData.screen_size}
                    onChange={(e) => setQuickModelData({ ...quickModelData, screen_size: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">{t('monitor.selectDefault')}</option>
                    {screenSizes.map(size => (
                      <option key={size} value={size}>{size}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('monitor.labelResolution')}
                  </label>
                  <select
                    value={quickModelData.resolution}
                    onChange={(e) => setQuickModelData({ ...quickModelData, resolution: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">{t('monitor.selectDefault')}</option>
                    {resolutionOptions.map(res => (
                      <option key={res} value={res}>{res}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('monitor.labelPanelType')}
                  </label>
                  <select
                    value={quickModelData.panel_type}
                    onChange={(e) => setQuickModelData({ ...quickModelData, panel_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">{t('monitor.selectDefault')}</option>
                    {panelTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('monitor.labelRefreshRate')}
                  </label>
                  <select
                    value={quickModelData.refresh_rate}
                    onChange={(e) => setQuickModelData({ ...quickModelData, refresh_rate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">{t('monitor.selectDefault')}</option>
                    <option value="60Hz">60Hz</option>
                    <option value="75Hz">75Hz</option>
                    <option value="100Hz">100Hz</option>
                    <option value="120Hz">120Hz</option>
                    <option value="144Hz">144Hz</option>
                    <option value="165Hz">165Hz</option>
                    <option value="240Hz">240Hz</option>
                  </select>
                </div>
              </div>

              <div className="flex flex-wrap gap-4">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={quickModelData.has_speaker}
                    onChange={(e) => setQuickModelData({ ...quickModelData, has_speaker: e.target.checked })}
                    className="rounded border-gray-300 dark:border-gray-600 text-primary focus:ring-primary"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">{t('monitor.hasSpeaker')}</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={quickModelData.has_usb_hub}
                    onChange={(e) => setQuickModelData({ ...quickModelData, has_usb_hub: e.target.checked })}
                    className="rounded border-gray-300 dark:border-gray-600 text-primary focus:ring-primary"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">{t('monitor.hasUsbHub')}</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={quickModelData.is_curved}
                    onChange={(e) => setQuickModelData({ ...quickModelData, is_curved: e.target.checked })}
                    className="rounded border-gray-300 dark:border-gray-600 text-primary focus:ring-primary"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">{t('monitor.isCurved')}</span>
                </label>
              </div>
            </div>

            <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowQuickModelModal(false);
                  setQuickModelData({
                    name: '',
                    screen_size: '',
                    resolution: '',
                    panel_type: '',
                    refresh_rate: '',
                    connectivity: '',
                    has_speaker: false,
                    has_usb_hub: false,
                    is_curved: false
                  });
                }}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={handleQuickModelSave}
                disabled={savingQuickModel || !quickModelData.name.trim()}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {savingQuickModel ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {t('monitor.saving')}
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    {t('monitor.addAndSelect')}
                  </>
                )}
              </button>
            </div>
          </div>
        </Modal>
    </div>
  );
};

export default MonitorsPage;
